import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, hasAuthParams } from 'react-oidc-context';
import {
  IS_LOCAL_ENV as isLocalEnv,
  validateReturnTo,
} from './constants/environment';
import Spinner from './components/atoms/Spinner/Spinner';
import AuthError from './components/molecules/AuthError/AuthError';

/**
 * Gets the full current URL path including pathname, search params, and hash.
 * Used to preserve complete URL state across authentication redirects.
 *
 * @returns {string} Full URL path (e.g., '/clients?filters=xyz#section')
 */
const getCurrentUrlPath = () => {
  return (
    window.location.pathname + window.location.search + window.location.hash
  );
};

const AuthenticatedRouter = ({ children }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Track if we've already handled post-auth navigation to prevent loops
  const hasNavigatedRef = useRef(false);

  // Handle redirect to IdP for unauthenticated users
  useEffect(() => {
    // Only redirect if:
    // 1. There are no auth params in the URL (prevents redirect loops)
    // 2. User is not authenticated
    // 3. Auth is not currently loading
    // 4. No active navigation is in progress
    // 5. Not local environment
    if (
      !isLocalEnv &&
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.isLoading &&
      !auth.activeNavigator
    ) {
      // Save the current URL (including query params and hash) in OIDC state to restore after login
      auth.signinRedirect({
        state: { returnTo: getCurrentUrlPath() },
      });
    }
  }, [auth]);

  // Handle post-authentication navigation using React Router
  // This ensures proper synchronization between browser URL and React Router state
  useEffect(() => {
    // Skip if local environment or already navigated
    if (isLocalEnv || hasNavigatedRef.current) {
      return;
    }

    // Only navigate after successful authentication when we have a returnTo
    if (auth.isAuthenticated && auth.user?.state?.returnTo) {
      const returnTo = validateReturnTo(auth.user.state.returnTo);
      const currentPath = location.pathname + location.search + location.hash;

      // Only navigate if we're not already at the target location
      if (currentPath !== returnTo) {
        hasNavigatedRef.current = true;
        // Use React Router's navigate to ensure proper state synchronization
        // This updates both browser URL and React Router's internal state
        navigate(returnTo, { replace: true });
      }
    }
  }, [auth.isAuthenticated, auth.user?.state?.returnTo, navigate, location]);

  // Reset navigation flag when user logs out
  useEffect(() => {
    if (!auth.isAuthenticated) {
      hasNavigatedRef.current = false;
    }
  }, [auth.isAuthenticated]);

  // Show loading state while:
  // 1. Initial auth state is loading
  // 2. Authentication is in progress
  // 3. Not local environment
  if (!isLocalEnv && (auth.isLoading || auth.activeNavigator)) {
    return (
      <div className="min-h-screen w-screen flex items-center justify-center">
        {/* <div className="text-lg">Loading...</div> */}
        <Spinner />
      </div>
    );
  }

  // Show error state if authentication failed and not local.
  // Preserve the original returnTo destination on retry
  if (!isLocalEnv && auth.error) {
    return (
      <AuthError
        handleClick={() => {
          // Preserve the original destination when retrying authentication
          auth.signinRedirect({
            state: { returnTo: getCurrentUrlPath() },
          });
        }}
      />
    );
  }

  // Only render routes if user is authenticated or if local
  if (isLocalEnv || auth.isAuthenticated) {
    return children;
  }

  // Return null while redirect is happening
  return null;
};

AuthenticatedRouter.displayName = 'AuthenticatedRouter';

// Prop types validation
AuthenticatedRouter.propTypes = {
  children: PropTypes.node.isRequired, // Children (content) are required to render inside the card
};

export default AuthenticatedRouter;
